import { useEffect, useState } from 'react';
import { useT } from '../../i18n';
import { api } from '../../api/client';
import { DropdownSelect } from '../../components/DropdownSelect';
import { projectDisplayCode } from '../../utils/identifiers';
import type { Project } from '../../types';
import { PROJECT_ROLES } from './projectRoles';
import { ProjectTeamRoles, projectTeam } from './ProjectTeamRoles';

// [Proposition: roles-projet] Proposals-page tab: a project's team with each
// person's role — the role's standard explanation kept apart from the
// project-specific précision — then the four roles and where they're used,
// for the PMO to review the wording.

// Where the roles come into play, in project-stage order (static keys for the i18n checker).
const STAGES = [
  { labelKey: 'roles.stage.cadrage.label', textKey: 'roles.stage.cadrage' },
  { labelKey: 'roles.stage.planning.label', textKey: 'roles.stage.planning' },
  { labelKey: 'roles.stage.execution.label', textKey: 'roles.stage.execution' },
  { labelKey: 'roles.stage.handover.label', textKey: 'roles.stage.handover' },
  { labelKey: 'roles.stage.resources.label', textKey: 'roles.stage.resources' },
];

export function RolesProposal() {
  const { t } = useT();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | ''>('');

  useEffect(() => {
    (api.getProjects() as Promise<Project[]>)
      .then(ps => {
        const sorted = [...ps].sort((a, b) => a.name.localeCompare(b.name));
        setProjects(sorted);
        const first = sorted.find(p => projectTeam(p).length > 0) ?? sorted[0];
        if (first) setProjectId(first.id);
      })
      .catch((e: Error) => alert(e.message));
  }, []);

  const project = projects.find(p => p.id === projectId);

  return (
    <div>
      <div className="section-label mb-8">{t('roles.teamTitle')}</div>
      <div className="toolbar mb-16">
        <DropdownSelect
          value={projectId}
          options={projects.map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` }))}
          placeholder={t('vjt.pickProject')}
          onChange={(v) => setProjectId(v === '' ? '' : Number(v))}
          triggerStyle={{ width: 360 }}
        />
      </div>

      {project && <div className="mb-24"><ProjectTeamRoles project={project} /></div>}

      <div className="section-label mb-8">{t('roles.definitionsTitle')}</div>
      <table className="table roles-table mb-24">
        <thead>
          <tr><th>{t('roles.colLevel')}</th><th>{t('roles.colRole')}</th><th>{t('roles.colDesc')}</th></tr>
        </thead>
        <tbody>
          {PROJECT_ROLES.map((r, i) => (
            <tr key={r.id}>
              <td>{i + 1}</td>
              <td>{t(r.labelKey)}</td>
              <td>{t(r.descKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="section-label mb-8">{t('roles.stagesTitle')}</div>
      <table className="table roles-stages">
        <tbody>
          {STAGES.map(s => (
            <tr key={s.textKey}>
              <td>{t(s.labelKey)}</td>
              <td>{t(s.textKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
